import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useTour } from "./TourContext";

const PAD = 8; // spotlight padding around target

function useTargetRect(testid) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!testid) { setRect(null); return; }
    let mounted = true;
    let attempts = 0;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el && mounted) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        setTimeout(() => {
          if (!mounted) return;
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }, 250);
      } else if (mounted && attempts < 30) {
        attempts += 1;
        setTimeout(measure, 150);
      } else if (mounted) {
        setRect(null); // target never appeared → centered card only
      }
    };
    measure();

    const onResize = () => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      mounted = false;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [testid]);

  return rect;
}

function cardPositionFor(rect, placement) {
  const CARD_W = 380;
  const CARD_H = 200;
  const margin = 16;
  if (!rect) {
    return {
      top: window.innerHeight / 2 - CARD_H / 2,
      left: window.innerWidth / 2 - CARD_W / 2,
    };
  }
  let top, left;
  if (placement === "top") {
    top = rect.top - CARD_H - margin;
    left = rect.left + rect.width / 2 - CARD_W / 2;
  } else if (placement === "left") {
    top = rect.top + rect.height / 2 - CARD_H / 2;
    left = rect.left - CARD_W - margin;
  } else if (placement === "right") {
    top = rect.top + rect.height / 2 - CARD_H / 2;
    left = rect.left + rect.width + margin;
  } else {
    // default: bottom
    top = rect.top + rect.height + margin;
    left = rect.left + rect.width / 2 - CARD_W / 2;
  }
  // Clamp
  const maxTop = window.innerHeight - CARD_H - 12;
  const maxLeft = window.innerWidth - CARD_W - 12;
  top = Math.max(12, Math.min(top, maxTop));
  left = Math.max(12, Math.min(left, maxLeft));
  return { top, left, width: CARD_W };
}

export default function TourOverlay() {
  const { active, tour, step, index, total, next, back, stop } = useTour();
  const rect = useTargetRect(step?.target);

  useEffect(() => {
    // Prevent body scroll while active.
    if (active) document.body.classList.add("tour-active");
    else document.body.classList.remove("tour-active");
    return () => document.body.classList.remove("tour-active");
  }, [active]);

  if (!active || !step || !tour) return null;

  const cardPos = cardPositionFor(rect, step.placement || "bottom");
  const isLast = index >= total - 1;

  return createPortal(
    <div className="tour-overlay" data-testid="tour-overlay" role="dialog" aria-live="polite">
      {rect ? (
        <>
          <div className="tour-mask tour-mask-top" style={{ height: Math.max(0, rect.top - PAD) }} />
          <div className="tour-mask tour-mask-bottom" style={{ top: rect.top + rect.height + PAD }} />
          <div className="tour-mask tour-mask-left" style={{ top: Math.max(0, rect.top - PAD), height: rect.height + PAD * 2, width: Math.max(0, rect.left - PAD) }} />
          <div className="tour-mask tour-mask-right" style={{ top: Math.max(0, rect.top - PAD), left: rect.left + rect.width + PAD, height: rect.height + PAD * 2 }} />
          <div className="tour-spotlight" style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }} data-testid="tour-spotlight" />
        </>
      ) : (
        <div className="tour-mask tour-mask-full" />
      )}

      <div className="tour-card" style={cardPos} data-testid="tour-card">
        <header>
          <div>
            <small>{tour.emoji} {tour.title} · Paso {index + 1} de {total}</small>
            <b>{step.title}</b>
          </div>
          <button type="button" onClick={stop} aria-label="Cerrar tutorial" data-testid="tour-close"><X size={16} /></button>
        </header>
        <p>{step.body}</p>
        <div className="tour-progress-bar" aria-hidden><div style={{ width: `${((index + 1) / total) * 100}%` }} /></div>
        <footer>
          <button type="button" onClick={back} disabled={index === 0} className="tour-btn ghost" data-testid="tour-prev">
            <ArrowLeft size={13} /> Atrás
          </button>
          {isLast ? (
            <button type="button" onClick={stop} className="tour-btn primary" data-testid="tour-finish">
              ¡Listo!
            </button>
          ) : (
            <button type="button" onClick={next} className="tour-btn primary" data-testid="tour-next">
              Siguiente <ArrowRight size={13} />
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
